import { useCollectionServices } from '../../../contexts/CollectionSourceContext';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Popover, Typography } from '@mui/material';
import { CloudDownloadOutlined, HelpOutline, Refresh } from '@mui/icons-material';
import { FitbitHistoryProgress } from '../../../services/fitbitCollectionDataService';
import { storedDateLabel } from './StoredDateRange';

function progressLabel(progress: FitbitHistoryProgress | null, sourceLabel = 'Fitbit') {
  if (!progress) return 'Waiting to start';
  if (progress.state === 'completed') return 'Complete';
  if (progress.state === 'held_for_review') return 'Needs attention';
  if (progress.state === 'connection_action_required') return `Reconnect ${sourceLabel}`;
  if (progress.controlState === 'paused') return 'Paused';
  if (progress.controlState !== 'active') return 'Stopped';
  if (progress.state === 'deferred') return 'Waiting to retry';
  if (progress.state === 'queued') return 'Queued';
  return 'Collecting';
}

const timeLabel = (value: string) => new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
// `since` is when the server accepted the request, or null when a daily check was already running.
const recentCheckFinished = (progress: FitbitHistoryProgress, since: string | null) => !progress.recentCheckRunning &&
  (!since || (!!progress.lastCheckedAt && Date.parse(progress.lastCheckedAt) >= Date.parse(since)));
// A requested check that ended without collecting. Scheduled collection is separate and unaffected.
const recentCheckFailed = (progress: FitbitHistoryProgress) => !progress.recentCheckRunning &&
  ['held_for_review', 'connection_action_required'].includes(progress.recentCheckState || '');
// Follow closely at first, then ease off: a check that reads many measurements can take minutes.
const followDelay = (attempt: number) => attempt < 10 ? 3000 : 10000;
const followAttempts = 37;

export default function ParticipantHistoryProgress({ studyId, participantId, name }: {
  studyId: string; participantId: string; name: string;
}) {
  const { readFitbitHistoryProgress, requestRecentCollection, source } = useCollectionServices();
  const root = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  const [refresh, setRefresh] = useState(0);
  const [progress, setProgress] = useState<FitbitHistoryProgress | null>();
  const [loading, setLoading] = useState(false);
  const [progressError, setProgressError] = useState(false);
  const [help, setHelp] = useState<HTMLElement | null>(null);
  const sourceLabel = source.label;
  const [following, setFollowing] = useState<{ since: string | null } | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);
  const participant = useRef(''); participant.current = `${studyId}:${participantId}`;

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !root.current) return;
    const observer = new IntersectionObserver(entries => setVisible(entries.some(entry => entry.isIntersecting)), { rootMargin: '100px' });
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { setProgress(undefined); setProgressError(false); setFollowing(null); setRequesting(false); setNotice(null); }, [studyId, participantId]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const load = async () => {
      if (document.hidden) { timer = setTimeout(load, 60000); return; }
      setLoading(true);
      try {
        const next = (await readFitbitHistoryProgress(studyId, participantId)).progress;
        if (cancelled) return;
        setProgress(next); setProgressError(false);
        if (following && next && recentCheckFailed(next)) {
          setFollowing(null);
          setNotice({ error: true, text: `${sourceLabel} could not provide today’s data just now. Scheduled collection is not affected; try again later.` });
        } else if (following && next && recentCheckFinished(next, following.since)) {
          setFollowing(null);
          setNotice({ text: `Up to date as of ${timeLabel(next.lastCheckedAt || new Date().toISOString())}. Readings synced so far are ready in Data extraction.` });
        } else if (following && ++attempts >= followAttempts) {
          setFollowing(null); setNotice({ text: 'Still collecting. Refresh this panel in a few minutes.' });
        }
      } catch (_) { if (!cancelled) setProgressError(true); }
      if (cancelled) return;
      setLoading(false);
      // Paused/held jobs can be recovered by an operator, so keep their status
      // fresh too. No polling is performed for hidden/off-screen participants.
      timer = setTimeout(load, following ? followDelay(attempts) : 60000);
    };
    void load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [studyId, participantId, visible, refresh, readFitbitHistoryProgress, following, sourceLabel]);

  const collectNow = async () => {
    const requested = participant.current;
    setRequesting(true); setNotice(null);
    try {
      const result = await requestRecentCollection(studyId, participantId);
      if (participant.current !== requested) return;
      if (result.status === 'recent') setNotice({ text: `Already checked${result.checkedAt ? ` at ${timeLabel(result.checkedAt)}` : ''}. Wait a minute before checking again.` });
      else setFollowing({ since: result.status === 'queued' ? result.requestedAt || null : null });
    } catch (error) {
      if (participant.current !== requested) return;
      // The server explains a refusal (paused, reconnect needed); other failures stay generic.
      const explained = (error as { status?: number }).status === 409 && error instanceof Error;
      setNotice({ error: true, text: explained ? (error as Error).message : 'Could not start collecting. Please try again.' });
    }
    setRequesting(false);
  };

  const percentage = progress ? Math.max(0, Math.min(100, Number(progress.percentage))) : 0;
  const label = progressLabel(progress || null, source.label);
  const checkedDates = progress?.checkedStart && progress.checkedEnd
    ? `Dates checked: ${storedDateLabel(progress.checkedStart)} to ${storedDateLabel(progress.checkedEnd)}`
    : progress?.checkedStart === null && progress?.checkedEnd === null
      ? 'Dates checked: No date ranges finished yet'
      : 'Historical collection'; // Older API versions still provide the percentage.
  const canCollectNow = source.supportsRecentCollection && progress?.state === 'completed' && progress.controlState === 'active';
  return <Box ref={root} aria-label={`Historical data for ${name}`} sx={{ px: 2, py: 1.25, mb: 0.5,
    border: '1px solid', borderColor: '#dfeae2', bgcolor: '#f7faf8', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="body2"><Box component="span" sx={{ fontWeight: 600 }}>{source.id === 'fitbit' ? 'Participant history: ' : `${source.label} history: `}</Box>
          {progress ? `${storedDateLabel(progress.historyStart)} to ${storedDateLabel(progress.historyEnd)}` :
            progressError ? 'Could not load history' : progress === null ? 'Waiting for collection to start' : 'Checking history…'}
        </Typography>
        {progress && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {checkedDates} ({percentage}% complete){progress.lastCheckedAt ? ` · Last checked ${timeLabel(progress.lastCheckedAt)}` : ''}
        </Typography>}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {canCollectNow && <Button size="small" variant="outlined" disabled={requesting || Boolean(following)} onClick={collectNow} sx={{ mr: 0.5 }}
          aria-label={`Get today’s data for ${name}`} startIcon={requesting || following ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadOutlined fontSize="small" />}>
          {following ? 'Collecting…' : 'Get today’s data'}</Button>}
        {progress !== undefined && <Chip label={label} size="small" color={label === 'Needs attention' || label.startsWith('Reconnect ') ? 'warning' : label === 'Complete' ? 'success' : 'default'} variant="outlined" />}
        <IconButton size="small" aria-label="About historical collection progress" onClick={event => setHelp(event.currentTarget)}><HelpOutline fontSize="small" /></IconButton>
        <IconButton size="small" aria-label={`Refresh historical data for ${name}`} disabled={loading} onClick={() => setRefresh(value => value + 1)}><Refresh fontSize="small" /></IconButton>
      </Box>
    </Box>
    {progress && <LinearProgress aria-label={`Historical collection for ${name}`} aria-valuenow={percentage} variant="determinate" value={percentage} color="success"
      sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: '#e2ebe5', '& .MuiLinearProgress-bar': { borderRadius: 3 } }} />}
    {(following || notice) && <Typography role="status" variant="caption" color={!following && notice?.error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.75 }}>
      {following ? `Collecting today’s and yesterday’s readings from ${source.label}…` : notice?.text}</Typography>}
    {progressError && <Typography role="status" variant="caption" color="error">Could not refresh collection progress. Use Refresh to retry; any progress shown is from the last successful check.</Typography>}
    <Popover open={Boolean(help)} anchorEl={help} onClose={() => setHelp(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Box sx={{ p: 2, maxWidth: 380 }}>
        <Typography variant="subtitle2">What these dates mean</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>{source.id === 'fitbit' ? 'Participant history is the range Fitbit allows StudySync to collect.' : 'Ultrahuman history is the date range being checked for this account.'} It does not mean the device was worn every day.</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>Dates checked come from finished collection windows, including daily updates. Some dates or measurements may have no readings. Open Data extraction to check available measurements.</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>The green bar shows historical collection completed, based on saved measurement windows. It is not a percentage of days with data or a time estimate. Daily updates do not increase it.</Typography>
        {source.supportsRecentCollection && <Typography variant="body2" sx={{ mt: 1 }}>New readings are normally collected once a day. Get today’s data checks yesterday and today right away, for example just after the participant syncs.</Typography>}
      </Box>
    </Popover>
  </Box>;
}
